import { find } from "lodash";
import moment from "moment";
import React, { useMemo } from "react";
import { useAuthentication } from "../../../context/authentication";
import { useMembers } from "../../../context/members";
import MemberAvatarImg from "../../../context/members/MemberAvatarImg";
import classNames from "../../../utils/classNames";
import { getMessageContentJsx } from "../../../utils/messageToJsx";
import { Comment } from "../../../utils/types";
import CenteredLoading from "../../CenteredLoading";

export interface Props {
  className?: string;
  comment: Comment;
  onEdit: () => void;
  onDelete: () => void;
  loadingDelete?: boolean;
}

const CommentComponent: React.FC<Props> = ({
  className,
  comment,
  onEdit,
  onDelete,
  loadingDelete = false
}) => {
  const { members } = useMembers();
  const { user } = useAuthentication();

  const createdAt = useMemo(
    () => moment(comment.created_at).utc().fromNow(),
    [comment.created_at]
  );

  const updatedAt = useMemo(
    () => moment(comment.updated_at).utc().fromNow(),
    [comment.updated_at]
  );

  const isCurrentUser = useMemo(
    () => user?.uuid === comment.user_uuid,
    [comment.user_uuid, user?.uuid]
  );

  const member = useMemo(
    () => find(members, { uuid: comment.user_uuid }),
    [comment.user_uuid, members]
  );

  const message = useMemo(
    () => getMessageContentJsx(comment.message),
    [comment.message]
  );

  return (
    <div
      id={`#${comment.uuid}`}
      className={classNames("flex space-x-3", className)}
    >
      <div className="flex-shrink-0">
        <MemberAvatarImg member={member} sizeInPx={40} />
      </div>
      <div>
        <div className="text-sm">
          <span className="font-medium text-gray-900 dark:text-white mr-1">
            {member?.name || comment.user_name || "System"}
          </span>
          <span className="font-medium text-gray-500 mx-1">
            {createdAt}
            {comment.updated_at &&
              comment.updated_at !== comment.created_at &&
              ` • edited ${updatedAt}`}
          </span>
        </div>

        <div className="mt-1 text-sm text-gray-700 dark:text-white">
          <p>{message}</p>
        </div>

        {isCurrentUser && (
          <div className="flex items-center text-sm mt-1">
            <button
              onClick={onEdit}
              type="button"
              className="text-gray-900 font-medium hover:underline dark:text-gray-300 mr-2 focus:outline-none"
            >
              Edit
            </button>
            {loadingDelete ? (
              <CenteredLoading className="block w-5 h-0 ml-1" />
            ) : (
              <button
                onClick={onDelete}
                type="button"
                className="text-gray-900 font-medium hover:underline dark:text-gray-300 focus:outline-none"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentComponent;
